import { generateHTML } from '@tiptap/html';
import { termExtensions } from './extensions';
import {
  TERM_BLANK,
  TERM_VARIABLE_NAMES,
  type TermVariableName,
  type TermVariableValues,
} from './variables';

/**
 * CSS do documento A4 — um só, usado no editor (folha na tela), no preview e no PDF, pra que o
 * que a secretaria vê seja o que sai impresso. Escopo em `.term-document`.
 */
export const TERM_DOCUMENT_CSS = `
.term-document {
  box-sizing: border-box;
  width: 210mm;
  min-height: 297mm;
  padding: 18mm 18mm;
  background: #ffffff;
  color: #1a1c16;
  font-family: 'DM Sans', ui-sans-serif, system-ui, sans-serif;
  font-size: 10.5pt;
  line-height: 1.45;
}
.term-document h1, .term-document h2, .term-document h3 {
  font-family: 'Barlow', ui-sans-serif, system-ui, sans-serif;
  font-weight: 800;
  line-height: 1.2;
  margin: 1.1em 0 0.5em;
  color: #0f120c;
}
.term-document h1 { font-size: 18pt; text-transform: uppercase; letter-spacing: -0.01em; }
.term-document h2 { font-size: 13.5pt; }
.term-document h3 { font-size: 12pt; }
.term-document p { margin: 0 0 0.6em; }
/* Linha em branco do editor sai como <p></p> (altura zero); o espaço mantém a altura de uma linha. */
.term-document p:empty::before { content: '\\00a0'; }
.term-document ul, .term-document ol { margin: 0 0 0.75em; padding-left: 1.4em; }
.term-document ul { list-style: disc; }
.term-document ol { list-style: decimal; }
.term-document li { margin: 0.2em 0; }
.term-document li p { margin: 0; }
.term-document blockquote { margin: 0 0 0.75em; padding-left: 1em; border-left: 3px solid #c9c7b4; }
.term-document hr { border: 0; border-top: 1px solid #c9c7b4; margin: 1.2em 0; }
.term-document .term-variable {
  display: inline-block;
  padding: 0 0.35em;
  border-radius: 4px;
  background: #eef3e2;
  color: #3d5a1e;
  font-weight: 600;
  font-size: 0.92em;
}
.term-document .term-filled { font-weight: 600; }
.term-document .term-blank { letter-spacing: 0.02em; }
`;

/**
 * JSON do editor → HTML, só com os nós conhecidos (texto é escapado pelo Tiptap). Lança erro se o
 * documento tiver nó desconhecido — é assim que o servidor recusa conteúdo inválido.
 */
export function renderTermHtml(content: Record<string, unknown>) {
  return generateHTML(content as any, termExtensions);
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Troca os chips `<span data-variable="x">…</span>` pelo valor (escapado) — ou por uma linha em
 * branco pra preencher à mão quando o valor não existe.
 */
export function fillTermVariables(html: string, values: TermVariableValues) {
  const fill = (name: string) => {
    const value = values[name as TermVariableName]?.toString().trim();
    return value
      ? `<span class="term-filled">${escapeHtml(value)}</span>`
      : `<span class="term-blank">${TERM_BLANK}</span>`;
  };

  return (
    html
      .replace(
        /<span[^>]*data-variable="([a-z_]+)"[^>]*>[\s\S]*?<\/span>/g,
        (_match, name: string) => fill(name),
      )
      // `{{nome}}` digitado como texto comum (não como chip) também é preenchido.
      .replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (match, name: string) =>
        (TERM_VARIABLE_NAMES as string[]).includes(name) ? fill(name) : match,
      )
  );
}

/**
 * Página HTML completa (A4) pronta pro motor de PDF. No papel as margens saem do `@page` (repetem
 * em toda página), não do padding da folha da tela — mesmos 18mm do editor.
 */
export function termDocumentPage(
  bodyHtml: string,
  title: string,
  { fontCss = '' }: { fontCss?: string } = {},
) {
  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
${fontCss}
@page { size: A4; margin: 18mm 18mm; }
html, body { margin: 0; padding: 0; background: #ffffff; }
${TERM_DOCUMENT_CSS}
.term-document { width: auto; min-height: 0; padding: 0; }
.term-document h1, .term-document h2, .term-document h3 { break-after: avoid; }
.term-document li, .term-document blockquote { break-inside: avoid; }
</style>
</head>
<body><div class="term-document">${bodyHtml}</div></body>
</html>`;
}

/** SHA-256 em hex — hash do conteúdo publicado e dos PDFs gerados. */
export async function sha256(data: string | Uint8Array) {
  const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join(
    '',
  );
}
